import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { managerSlug, slugToManager } from '../utils/slug';
import {
  ACTIVE_MEMBERS,
  ALL_MEMBERS,
  getManagerTagline,
  getManagerDescription,
} from '../utils/managers';
import { loadCareerRankings } from '../utils/managerStats';
import { loadManagerGameStats } from '../utils/managerGameStats';
import { loadChampionships, ringYearsByManager } from '../utils/championships';
import { loadRivalriesFor } from '../utils/rivalries';
import { loadRoasts, managerRoasts } from '../utils/roasts';
import { loadManagerSeasons, seasonExtremesToBestWorst } from '../utils/managerSeasons';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import QuoteCard from '../components/ui/QuoteCard';
import StatBlock from '../components/ui/StatBlock';
import CareerStatistics from '../components/profile/CareerStatistics';
import ManagerMilestones from '../components/profile/ManagerMilestones';
import { buildManagerMilestone } from '../utils/recordsUnderAttack';
import { keeperEraQuestionFor } from '../data/profileEditorial';
import CastFigure from '../motion/CastFigure';
import Depth from '../motion/Depth';
import Reveal from '../motion/Reveal';

// Hardcoded year ranges per manager. Active members get the full completed
// league history through 2025.
// Alumni get a manual range based on when they played.
const ALUMNI_YEARS = {
  Blake: [2013, 2014, 2015, 2016, 2017],
  'Michael R': [2013, 2014],
  Alex: [2013],
  'Harrison Williams': [2013],
};

// Legacy editorial fallback for managers whose per-season CSV cannot load.
// Normal profiles use seasonExtremesToBestWorst() from the validated CSV data.
const BEST_WORST = {
  Kyle: {
    best: {
      title: '2014. The first ring.',
      blurb: '11-2 regular season. Highest points-for in the league. Took the 2014 title and set the tone for a decade — the league\'s first dynasty was no accident.',
      record: '11-2', pointsFor: '1,683', differential: '+402',
    },
    worst: {
      title: '2022. The collapse.',
      blurb: 'Three straight losses to close the year. Sixth place. Watched Robby take the replayed championship from his couch. The Dynasty King looked human for the first time in eight years.',
      record: '5-8', pointsFor: '1,422', differential: '−118',
    },
  },
  Anna: {
    best: {
      title: '2025. The third ring.',
      blurb: 'Back-to-back titles. Dropped 214.83 on Robby in the championship — a 107.36-point blowout, the second-largest title-game margin ever. The Phemale Phenom pulled even with Kyle for the all-time ring lead.',
      record: '9-5', pointsFor: '2,623', differential: '+229',
    },
    worst: {
      title: '2017. The dry year.',
      blurb: 'Out of the playoffs. Four-game losing streak in the second half. The only season in a decade she watched the championship from the sidelines.',
      record: '6-7', pointsFor: '1,401', differential: '−42',
    },
  },
  Robby: {
    best: {
      title: '2018. The Commissioner wins the regular season.',
      blurb: 'Commissioner\'s Army went 10-3 and earned the regular-season crown before Kyle took the playoff final. Robby later won his playoff championship in 2022.',
      record: '10-3', pointsFor: '2,556', differential: '+160',
    },
    worst: {
      title: '2020. The COVID year.',
      blurb: 'Punted the back half. Lost five in a row. Spent more time citing the rulebook than scoring points. The commissioner does not get a participation trophy.',
      record: '5-8', pointsFor: '1,389', differential: '−112',
    },
  },
  'A.J.': {
    best: {
      title: '2021. The Gunslinger gets his ring.',
      blurb: 'A career-high in points, a career-high in confidence, and a championship to shove in Robby\'s face for the rest of his life. The lone year A.J. cashed in.',
      record: '9-4', pointsFor: '1,597', differential: '+201',
    },
    worst: {
      title: '2015. The bottom.',
      blurb: 'Five wins. Forty-fifth percentile points-for. Spent the offseason "preparing." The preparation took six more years to pay off.',
      record: '5-8', pointsFor: '1,335', differential: '−146',
    },
  },
  Amir: {
    best: {
      title: '2019. Operates on a different frequency.',
      blurb: 'Won the title with the second-lowest points-for in the bracket. Strategy over scoreboard. The Strategic Mind got his ring on math, not muscle.',
      record: '9-4', pointsFor: '1,489', differential: '+92',
    },
    worst: {
      title: '2015. The reset.',
      blurb: 'Four wins. The frequency went dark. Came back stronger every year after; this one was the floor.',
      record: '4-9', pointsFor: '1,267', differential: '−214',
    },
  },
  Kevin: {
    best: {
      title: '2023. Basement to crown.',
      blurb: 'Last place in 2022. Trophy in 2023. The single best worst-to-first run in league history — and the only reason anyone keeps checking on him.',
      record: '9-4', pointsFor: '1,572', differential: '+178',
    },
    worst: {
      title: '2022. Last place.',
      blurb: 'Worst record. Worst points-for. Worst luck. The season that made the 2023 ring feel like a magic trick.',
      record: '3-10', pointsFor: '1,201', differential: '−311',
    },
  },
  Samuel: {
    best: {
      title: '2018. The almost.',
      blurb: 'Top-three regular-season finish. First-round exit. Eight seasons of "this is the year" got within one matchup of being right.',
      record: '8-5', pointsFor: '1,512', differential: '+91',
    },
    worst: {
      title: '2020. The math hates him.',
      blurb: 'Outscored more weeks than he won. The Eternal Optimist learned that schedule luck is real and it does not like him.',
      record: '4-9', pointsFor: '1,389', differential: '−54',
    },
  },
  Bryan: {
    best: {
      title: '2021. The Grinder peaks.',
      blurb: 'Career-best points-for. Made the playoff bracket. Lost in the first round to the eventual champion. Twelve years of grinding, one playoff-week swing from a ring.',
      record: '10-3', pointsFor: '1,621', differential: '+241',
    },
    worst: {
      title: '2015. The floor.',
      blurb: 'Two wins. Punted September. The only year the grind didn\'t.',
      record: '2-11', pointsFor: '1,121', differential: '−418',
    },
  },
  Josh: {
    best: {
      title: '2022. Almost Daddy.',
      blurb: '"Daddy" came one game from the title shot. Top-four finish, top-three points-for. The closest the hunt has ever gotten to the kill.',
      record: '8-5', pointsFor: '1,533', differential: '+118',
    },
    worst: {
      title: '2016. The lost year.',
      blurb: 'Last place. Fired the trainer, fired the analyst, fired himself. Rebuilt from scratch the next spring.',
      record: '3-10', pointsFor: '1,212', differential: '−289',
    },
  },
  Ryan: {
    best: {
      title: '2020. The Rhino charges.',
      blurb: 'Best regular season of any non-champion in league history. Won the regular-season title, lost the playoffs, and has been dining out on the bitterness ever since.',
      record: '11-2', pointsFor: '1,668', differential: '+312',
    },
    worst: {
      title: '2017. The off year.',
      blurb: 'Out of the bracket. Out of the trash talk. The one season the Rhino didn\'t charge.',
      record: '5-8', pointsFor: '1,378', differential: '−92',
    },
  },
  Blake: {
    best: {
      title: '2016. The first ring.',
      blurb: 'Blake went 9-4 and won the first of two consecutive championships. He drafted Hatley\'s starting 5 the following year; Robby helped complete that 2017 title season after Blake\'s passing.',
      record: '9-4', pointsFor: '2,464', differential: '+337',
    },
    worst: {
      title: '2014. The lesson.',
      blurb: 'Sub-.500 in year two. Watched Kyle take 2014. Spent the next two years figuring out how to take a title of his own.',
      record: '5-8', pointsFor: '1,298', differential: '−104',
    },
  },
  'Michael R': {
    best: {
      title: '2014. The exit.',
      blurb: 'Two seasons. One legend. Showed up in the La Liga year, played 2014 out, and left a story that still gets told at every draft.',
      record: '6-7', pointsFor: '1,367', differential: '−18',
    },
    worst: {
      title: '2014. Also the exit.',
      blurb: 'Same season, same record. Two years and gone. One of the league\'s shortest tenures and its longest mythology.',
      record: '6-7', pointsFor: '1,367', differential: '−18',
    },
  },
};

function bestWorstFor(name, seasons, championshipYears) {
  const fromCsv = seasonExtremesToBestWorst(name, seasons, championshipYears);
  if (fromCsv) return fromCsv;
  if (BEST_WORST[name]) return BEST_WORST[name];
  const tagline = getManagerTagline(name) || 'A career';
  return {
    best: {
      title: `${tagline}. The peak.`,
      blurb: `The high-water mark of ${name}'s career. Season-level data is unavailable for this profile, so the Almanac falls back to the career ledger.`,
      record: '—', pointsFor: '—', differential: '—',
    },
    worst: {
      title: `${tagline}. The valley.`,
      blurb: `The hard season every manager has. Season-level data is unavailable for this profile, so the Almanac falls back to the career ledger.`,
      record: '—', pointsFor: '—', differential: '—',
    },
  };
}

function ProfileSnapshotCard({ label, title, body, accent = 'gold' }) {
  const borderClass = accent === 'red' ? 'border-accent-red' : 'border-accent-gold';
  const labelClass = accent === 'red' ? 'text-accent-red' : 'text-accent-gold';

  return (
    <article className={`bg-bg-surface p-5 border-t-[3px] ${borderClass}`}>
      <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-3 ${labelClass}`}>
        {label}
      </div>
      <div className="text-lg md:text-xl font-black leading-tight tracking-normal">
        {title}
      </div>
      <p className="mt-2 text-[12px] md:text-[13px] text-ink-secondary leading-relaxed italic">
        {body}
      </p>
    </article>
  );
}

function yearsFor(name, seasons = []) {
  if (ALUMNI_YEARS[name]) return ALUMNI_YEARS[name];
  if (ACTIVE_MEMBERS.includes(name)) {
    return seasons
      .map((season) => season.year)
      .filter(Number.isFinite)
      .sort((first, second) => first - second);
  }
  return [];
}

function withTerminalPeriod(value) {
  return /[.!?]$/.test(value) ? value : `${value}.`;
}

export default function ManagerProfile() {
  const { slug } = useParams();
  const name = slugToManager(slug, ALL_MEMBERS);
  const [stats, setStats] = useState(null);
  const [careerRankings, setCareerRankings] = useState([]);
  const [championshipYears, setChampionshipYears] = useState([]);
  const [rivalries, setRivalries] = useState([]);
  const [peerQuotes, setPeerQuotes] = useState([]);
  const [managerFacts, setManagerFacts] = useState([]);
  const [seasonRows, setSeasonRows] = useState([]);
  const [gameStats, setGameStats] = useState(null);

  useEffect(() => {
    loadCareerRankings()
      .then((rankings) => {
        setCareerRankings(rankings);
        const found = rankings.find((r) => r.manager === name);
        setStats(found || null);
      })
      .catch(() => {
        setCareerRankings([]);
        setStats(null);
      });
  }, [name]);

  useEffect(() => {
    loadChampionships()
      .then((c) => {
        const map = ringYearsByManager(c);
        setChampionshipYears(map[name] || []);
      })
      .catch(() => setChampionshipYears([]));
  }, [name]);

  useEffect(() => {
    if (!name) return;
    loadRivalriesFor(name)
      .then((rs) => setRivalries(rs.filter((r) => r.total_games > 0)))
      .catch(() => setRivalries([]));
  }, [name]);

  useEffect(() => {
    if (!name) return;
    fetch('/data/peer_quotes.json')
      .then((r) => r.json())
      .then((all) => setPeerQuotes(all[name] || []))
      .catch(() => setPeerQuotes([]));
  }, [name]);

  useEffect(() => {
    if (!name) return;
    loadRoasts()
      .then((all) => setManagerFacts(managerRoasts(all, name, 4)))
      .catch(() => setManagerFacts([]));
  }, [name]);

  useEffect(() => {
    if (!name) return;
    loadManagerSeasons(name)
      .then(setSeasonRows)
      .catch(() => setSeasonRows([]));
  }, [name]);

  useEffect(() => {
    if (!name) return;
    loadManagerGameStats(name)
      .then(setGameStats)
      .catch(() => setGameStats(null));
  }, [name]);

  if (!name) {
    return (
      <div className="px-6 md:px-14 py-20">
        <h1 className="text-display-md font-black tracking-normal">Manager not found.</h1>
      </div>
    );
  }

  const tagline = getManagerTagline(name);
  const description = getManagerDescription(name);
  const isActive = ACTIVE_MEMBERS.includes(name);

  const rank = stats?.rank;
  const eyebrowLabel = isActive && rank
    ? `ROSTER · MANAGER #${String(rank).padStart(2, '0')}`
    : isActive
      ? 'ROSTER · ACTIVE'
      : 'ROSTER · ALUMNUS';

  const rings = stats?.stats?.championships ?? 0;
  const wins = stats?.stats?.total_wins ?? 0;
  const losses = stats?.stats?.total_losses ?? 0;
  const winPct = stats?.stats?.win_percentage ?? 0;
  const seasons = stats?.stats?.seasons_played ?? 0;

  const years = yearsFor(name, seasonRows);
  const championshipSet = new Set(championshipYears);
  const ringWord = championshipYears.length === 1 ? 'ring' : 'rings';
  const bestWorst = bestWorstFor(name, seasonRows, championshipYears);
  const milestone = isActive ? buildManagerMilestone(name, careerRankings, seasonRows) : null;

  const topRivalries = rivalries.slice(0, 5);
  const rivalrySample = rivalries.filter((r) => r.total_games >= 3);
  const favoriteTarget = rivalrySample.slice().sort((a, b) => b.win_rate - a.win_rate || b.total_games - a.total_games)[0] || null;
  const biggestProblem = rivalrySample.slice().sort((a, b) => a.win_rate - b.win_rate || b.total_games - a.total_games)[0] || null;
  const rivalryTotals = rivalries.reduce(
    (acc, r) => ({
      games: acc.games + r.total_games,
      wins: acc.wins + r.wins,
    }),
    { games: 0, wins: 0 },
  );

  // Prev/Next nav across ACTIVE_MEMBERS only. Alumni profiles get the
  // central CTA without arrow links.
  const idx = ACTIVE_MEMBERS.indexOf(name);
  const prevName = idx > 0
    ? ACTIVE_MEMBERS[idx - 1]
    : idx === 0
      ? ACTIVE_MEMBERS[ACTIVE_MEMBERS.length - 1]
      : null;
  const nextName = idx >= 0 && idx < ACTIVE_MEMBERS.length - 1
    ? ACTIVE_MEMBERS[idx + 1]
    : idx === ACTIVE_MEMBERS.length - 1
      ? ACTIVE_MEMBERS[0]
      : null;

  return (
    <>
      {/* HERO */}
      <HeroBackground backdrop="studio" variant="gold">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,520px)] lg:items-end lg:justify-between">
          {/* LEFT */}
          <div className="flex flex-col justify-center px-6 py-14 md:px-14 md:py-16">
            <Reveal>
              <Eyebrow color="gold" className="mb-3.5 tracking-[0.5em]">
                {eyebrowLabel}
              </Eyebrow>
              <h1 className="mb-2 font-display text-display-lg font-black uppercase leading-[0.85] tracking-normal md:text-display-xl">
                {withTerminalPeriod(name.toUpperCase())}
              </h1>
              <p className="mb-8 max-w-xl font-body text-xl font-light italic leading-snug text-ink-secondary md:text-2xl">
                "{tagline}." {description}
              </p>

              {/* Résumé strip */}
              <div className="grid grid-cols-2 items-end gap-7 md:grid-cols-4 md:gap-8">
                <StatBlock value={rings} label="RINGS" color="gold" size="lg" />
                <StatBlock
                  value={`${wins}-${losses}`}
                  label="CAREER"
                  size="lg"
                />
                <StatBlock
                  value={
                    <>
                      {Math.round(winPct)}
                      <span className="text-3xl">%</span>
                    </>
                  }
                  label="WIN RATE"
                  size="lg"
                />
                <StatBlock value={seasons} label="SEASONS" size="lg" />
              </div>
            </Reveal>
          </div>

          {/* RIGHT */}
          <div className="px-6 pb-10 md:px-14 lg:px-8 lg:pb-0 lg:pr-14">
            {isActive ? (
              <Depth className="depth--3d mx-auto w-full max-w-[460px]" strength={0.45}>
                <div className="paper relative border border-[#ded4c5] px-3 pt-5" data-depth="1">
                  <Reveal className="relative z-[1]">
                    <CastFigure
                      name={name}
                      trigger="hover"
                      sequence={['shock', 'point', 'celebrate', 'smug']}
                      className="aspect-[4/5] w-full"
                      imgClassName="object-contain object-bottom"
                      alt={`${name} caricature`}
                    />
                  </Reveal>
                  {rank && (
                    <div className="absolute bottom-5 right-4 z-10 bg-accent-gold px-3.5 py-2 font-mono text-[9px] font-black tracking-[0.2em] text-bg-base sm:bottom-6 sm:right-6 sm:text-[10px] sm:tracking-[0.25em]">
                      CURRENT POWER #{String(rank).padStart(2, '0')}
                    </div>
                  )}
                </div>
              </Depth>
            ) : (
              <div className="paper mx-auto flex aspect-[4/5] w-full max-w-[460px] items-center justify-center border border-[#ded4c5]">
                <ManagerAvatar name={name} size="hero" framed />
              </div>
            )}
          </div>
        </div>
      </HeroBackground>

      <section className="px-8 md:px-14 py-12 border-b border-border bg-bg-base">
        <Reveal className="mb-7 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Eyebrow color="gold" className="mb-2 tracking-[0.5em]">PROFILE SNAPSHOT</Eyebrow>
            <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none">
              The quick read.
            </h2>
          </div>
          <div className="text-[10px] text-ink-tertiary tracking-[0.2em] italic">
            Identity · trophy math · rivalry pressure · keeper question
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3.5">
          <ProfileSnapshotCard
            label="IDENTITY"
            title={tagline}
            body={description}
          />
          <ProfileSnapshotCard
            label="TROPHY CASE"
            title={rings > 0 ? `${rings} ${ringWord}` : 'No rings yet'}
            body={championshipYears.length > 0
              ? `Championship years: ${championshipYears.join(', ')}.`
              : 'The resume still has a blank space where the title should be.'}
            accent={rings > 0 ? 'gold' : 'red'}
          />
          <ProfileSnapshotCard
            label="RIVALRY PRESSURE"
            title={favoriteTarget ? `${favoriteTarget.wins}-${favoriteTarget.losses} vs ${favoriteTarget.opponent}` : 'Still loading'}
            body={biggestProblem
              ? `Best edge: ${favoriteTarget.opponent}. Biggest problem: ${biggestProblem.opponent} (${biggestProblem.wins}-${biggestProblem.losses}).`
              : 'Head-to-head records load from the rivalry ledger.'}
          />
          <ProfileSnapshotCard
            label="KEEPER ERA QUESTION"
            title="What changes now?"
            body={keeperEraQuestionFor(name)}
            accent="red"
          />
        </div>
      </section>

      <CareerStatistics
        name={name}
        career={stats?.stats}
        seasons={seasonRows}
        gameStats={gameStats}
        championshipYears={championshipYears}
      />

      <ManagerMilestones milestone={milestone} />

      {/* CHAMPIONSHIP TIMELINE */}
      {years.length > 0 && (
        <section className="px-8 md:px-14 py-14 border-b border-border bg-bg-deep">
          <Reveal>
            <Eyebrow color="gold" className="mb-2 tracking-[0.5em]">THE LEDGER</Eyebrow>
            <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none mb-9">
              {years.length} season{years.length === 1 ? '' : 's'}, {championshipYears.length} {ringWord}, zero apologies.
            </h2>
          </Reveal>

          {/* Timeline rail */}
          <div className="relative pt-7">
            <div className="absolute left-0 right-0 top-[42px] h-[2px] bg-border" />
            <div
              className="grid gap-0 relative"
              style={{ gridTemplateColumns: `repeat(${years.length}, minmax(0, 1fr))` }}
            >
              {years.map((year) => {
                const isChamp = championshipSet.has(year);
                return (
                  <div key={year} className="text-center relative">
                    <div className={`text-2xl mb-2 ${isChamp ? 'text-accent-gold' : 'text-transparent'}`}>
                      {isChamp ? '◆' : '.'}
                    </div>
                    <div
                      className={`${isChamp ? 'w-3.5 h-3.5 bg-accent-gold' : 'w-2.5 h-2.5 bg-ink-muted'} rounded-full mx-auto border-[3px] border-bg-deep`}
                    />
                    <div
                      className={`text-[11px] font-black tracking-[0.1em] mt-2.5 ${isChamp ? 'text-accent-gold' : 'text-ink-tertiary'}`}
                    >
                      {year}
                    </div>
                    <div
                      className={`text-[9px] tracking-[0.1em] mt-0.5 ${isChamp ? 'text-ink-tertiary' : 'text-ink-muted'}`}
                    >
                      {isChamp ? 'CHAMPION' : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* BEST / WORST */}
      <section className="grid grid-cols-1 md:grid-cols-2 border-b border-border">
        {/* BEST */}
        <div className="p-12 md:p-14 bg-gradient-to-br from-bg-surface to-bg-elevated">
          <Reveal>
            <Eyebrow color="gold" className="mb-3 tracking-[0.4em]">
              THE BEST OF {name.toUpperCase()}
            </Eyebrow>
            <h3 className="text-2xl md:text-3xl font-black tracking-normal leading-[1.05] mb-4">
              {bestWorst.best.title}
            </h3>
          </Reveal>
          <p className="text-sm text-ink-secondary leading-relaxed font-light mb-6">
            {bestWorst.best.blurb}
          </p>
          <div className="grid grid-cols-3 gap-5">
            <StatBlock value={bestWorst.best.record} label="RECORD" color="gold" size="sm" />
            <StatBlock value={bestWorst.best.pointsFor} label="POINTS FOR" color="gold" size="sm" />
            <StatBlock value={bestWorst.best.differential} label="DIFFERENTIAL" color="gold" size="sm" />
          </div>
        </div>

        {/* WORST */}
        <div className="p-12 md:p-14 bg-bg-deep">
          <Reveal>
            <Eyebrow color="red" className="mb-3 tracking-[0.4em]">
              THE WORST OF {name.toUpperCase()}
            </Eyebrow>
            <h3 className="text-2xl md:text-3xl font-black tracking-normal leading-[1.05] mb-4">
              {bestWorst.worst.title}
            </h3>
          </Reveal>
          <p className="text-sm text-ink-secondary leading-relaxed font-light mb-6">
            {bestWorst.worst.blurb}
          </p>
          <div className="grid grid-cols-3 gap-5">
            <StatBlock value={bestWorst.worst.record} label="RECORD" color="red" size="sm" />
            <StatBlock value={bestWorst.worst.pointsFor} label="POINTS FOR" color="red" size="sm" />
            <StatBlock value={bestWorst.worst.differential} label="DIFFERENTIAL" color="red" size="sm" />
          </div>
        </div>
      </section>

      {/* DATA DOSSIER */}
      {managerFacts.length > 0 && (
        <section className="px-8 md:px-14 py-14 border-b border-border bg-bg-base">
          <Reveal className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <Eyebrow color="gold" className="mb-2 tracking-[0.5em]">
                THE DOSSIER
              </Eyebrow>
              <h2 className="font-display text-display-xs md:text-display-sm font-black tracking-normal leading-none">
                Four verified charges.
              </h2>
            </div>
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em] italic">
              Mined from league CSVs · no filler
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3.5">
            {managerFacts.map((fact, i) => (
              <article
                key={`${fact.category}-${i}`}
                className={`bg-bg-surface p-5 border-t-[3px] ${
                  i === 0 ? 'border-accent-gold' : i === 1 ? 'border-accent-red' : 'border-ink-tertiary'
                }`}
              >
                <div className="text-[9px] text-accent-gold tracking-[0.25em] font-extrabold mb-3 uppercase">
                  Exhibit {String(i + 1).padStart(2, '0')}
                </div>
                <p className="text-sm md:text-[15px] text-ink-secondary leading-relaxed italic">
                  {fact.text}
                </p>
                {fact.category && (
                  <div className="mt-4 text-[9px] text-ink-tertiary tracking-[0.18em] font-bold uppercase">
                    {fact.category.replace(/_/g, ' ')}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* HEAD TO HEAD */}
      {topRivalries.length > 0 && (
        <section className="px-8 md:px-14 py-14 border-b border-border">
          <Reveal className="mb-8 flex flex-col gap-3 md:flex-row md:items-baseline md:justify-between">
            <div>
              <Eyebrow color="gold" className="mb-2 tracking-[0.5em]">HEAD TO HEAD</Eyebrow>
              <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none">
                The blood feuds.
              </h2>
            </div>
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em] italic">
              {rivalries.length} rivals · {rivalryTotals.games} career matchups · {rivalryTotals.wins} wins
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {topRivalries.map((r) => {
              const isDominant = r.win_rate >= 70;
              const isKryptonite = r.win_rate <= 30;
              const accent = isDominant
                ? 'gold'
                : isKryptonite
                  ? 'red'
                  : 'muted';
              const borderClass = {
                gold: 'border-t-[3px] border-accent-gold',
                red: 'border-t-[3px] border-accent-red',
                muted: 'border-t-[3px] border-ink-tertiary',
              }[accent];
              const label = isDominant ? 'DOMINANT' : isKryptonite ? 'KRYPTONITE' : 'EVEN';
              const labelColor = {
                gold: 'text-accent-gold',
                red: 'text-accent-red',
                muted: 'text-ink-tertiary',
              }[accent];
              const recordColor = {
                gold: 'text-accent-gold',
                red: 'text-accent-red',
                muted: 'text-ink-primary',
              }[accent];

              return (
                <Link
                  key={r.opponent}
                  to={`/managers/${managerSlug(r.opponent)}`}
                  className={`block bg-bg-surface p-5 text-center ${borderClass} hover:bg-bg-elevated transition-colors`}
                >
                  <div className="flex justify-center mb-3">
                    <ManagerAvatar name={r.opponent} size="md" />
                  </div>
                  <div className="text-sm font-black">{r.opponent.toUpperCase()}</div>
                  <div className={`text-[9px] tracking-[0.2em] font-bold mt-1 ${labelColor}`}>
                    {label}
                  </div>
                  <div className={`text-2xl font-black mt-2.5 leading-none ${recordColor}`}>
                    {r.wins}-{r.losses}
                  </div>
                  <div className="text-[9px] text-ink-tertiary tracking-[0.1em] mt-1">
                    {r.win_rate.toFixed(1)}%
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* PEER REVIEW */}
      {peerQuotes.length > 0 && (
        <section className="px-8 md:px-14 py-14 border-b border-border bg-gradient-to-b from-bg-base to-bg-surface">
          <Reveal className="mb-9 text-center">
            <div className="flex justify-center mb-1.5">
              <Eyebrow color="red" className="tracking-[0.5em]">THE PEER REVIEW</Eyebrow>
            </div>
            <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none">
              What they say about {withTerminalPeriod(name)}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {peerQuotes.map((q, i) => (
              <QuoteCard
                key={i}
                quote={q.quote}
                manager={q.speaker}
                subtitle={q.subtitle}
                accent={q.accent}
              />
            ))}
          </div>
        </section>
      )}

      {/* PREV / NEXT NAV */}
      <section className="px-6 md:px-14 py-9 bg-bg-deep grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-4 items-center">
        {prevName ? (
          <Link
            to={`/managers/${managerSlug(prevName)}`}
            className="flex gap-3.5 items-center group min-w-0"
          >
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em] group-hover:text-accent-gold">←</div>
            <ManagerAvatar name={prevName} size="sm" />
            <div className="min-w-0">
              <div className="text-[9px] text-ink-tertiary tracking-[0.2em]">PREVIOUS</div>
              <div className="text-sm font-black group-hover:text-accent-gold transition-colors truncate">
                {prevName.toUpperCase()}
              </div>
            </div>
          </Link>
        ) : (
          <div className="hidden sm:block" />
        )}

        <Link
          to="/managers"
          className="justify-self-start sm:justify-self-center text-[10px] text-accent-gold tracking-[0.25em] font-extrabold border border-accent-gold px-4 py-2.5 hover:bg-accent-gold hover:text-bg-base transition-colors whitespace-nowrap"
        >
          VIEW ALL MANAGERS
        </Link>

        {nextName ? (
          <Link
            to={`/managers/${managerSlug(nextName)}`}
            className="flex gap-3.5 items-center group min-w-0 sm:justify-self-end"
          >
            <div className="text-right min-w-0">
              <div className="text-[9px] text-ink-tertiary tracking-[0.2em]">NEXT</div>
              <div className="text-sm font-black group-hover:text-accent-gold transition-colors truncate">
                {nextName.toUpperCase()}
              </div>
            </div>
            <ManagerAvatar name={nextName} size="sm" />
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em] group-hover:text-accent-gold">→</div>
          </Link>
        ) : (
          <div />
        )}
      </section>
    </>
  );
}
